import { css } from '@emotion/react'
import { Button, Cell, Flow, Grid, HeadingSection, Icon, Text } from 'bold-ui'
import {
  CepField,
  CheckboxField,
  EquipeSelectField,
  Form,
  FormRenderProps,
  PecFormApi,
  resetAllFieldStates,
  SubmitButton,
  TextField,
  UnidadeSaudeSelectField,
} from 'components/form'
import { MicroareaImovelSelectField } from 'components/form/field/select/MicroareaImovelSelectField/MicroareaImovelSelectField'
import { TipoEnderecoEnum } from 'graphql/types.generated'
import { MutableRefObject, useMemo } from 'react'
import { metaPath } from 'util/metaPath'
import { isEmpty } from 'util/validation/Util'

import { createInativacaoImoveisBuscaFormCalculator } from './calculator-inativacaoImoveis'
import { InativacaoImoveisBuscaFormModel } from './model-inativacaoImoveis'
import { validateInativacaoImoveisBuscaForm } from './validator-inativacaoImoveis'

const meta = metaPath<InativacaoImoveisBuscaFormModel>()

const INITIAL_VALUES: InativacaoImoveisBuscaFormModel = { ocultarImoveisComFamilia: true }

interface InativacaoImoveisBuscaFormProps {
  formRef: MutableRefObject<PecFormApi<InativacaoImoveisBuscaFormModel> | undefined>
  onSubmit(formValues: InativacaoImoveisBuscaFormModel): void
  onLimparCampos(): void
  disabled?: boolean
}

export function InativacaoImoveisBuscaForm({
  formRef,
  onSubmit,
  onLimparCampos,
  disabled,
}: Readonly<InativacaoImoveisBuscaFormProps>) {
  const decorators = useMemo(() => [createInativacaoImoveisBuscaFormCalculator(meta)], [])

  const handleLimparCampos = (form: PecFormApi<InativacaoImoveisBuscaFormModel>) => {
    resetAllFieldStates(form)
    form.reset(INITIAL_VALUES)
    onLimparCampos()
  }

  const renderForm = ({ handleSubmit, form, values }: FormRenderProps<InativacaoImoveisBuscaFormModel>) => {
    formRef.current = form

    return (
      <HeadingSection
        level={2}
        title='Endereço dos imóveis'
        vSpace={5}
        style={css`
          margin-top: 1rem;
        `}
      >
        <Text>
          Preencha os campos para realizar uma busca nas Fichas de Cadastro Domiciliar e Territorial e atualize as
          informações.
        </Text>
        <Grid
          wrap
          style={css`
            margin-top: 1rem;
          `}
        >
          <Cell size={4}>
            <TextField name={meta.logradouro} label='Logradouro' maxLength={72} disabled={disabled} />
          </Cell>
          <Cell size={2}>
            <CepField name={meta.cep} label='CEP' disabled={disabled} />
          </Cell>
          <Cell size={6}>
            <div
              css={css`
                margin-top: 25px;
              `}
            >
              <CheckboxField
                name={meta.ocultarImoveisComFamilia}
                label='Ocultar imóveis com famílias'
                disabled={disabled}
              />
            </div>
          </Cell>
          <Cell size={3}>
            <UnidadeSaudeSelectField
              name={meta.unidadeSaude}
              label='Unidade de saúde'
              mostrarInativas
              dataTestId='UnidadeSaudeSelectField.InativacaoImoveis'
              disabled={disabled}
            />
          </Cell>
          <Cell size={2}>
            <EquipeSelectField
              name={meta.equipe}
              label='Equipe'
              unidadeSaudeId={values?.unidadeSaude?.id}
              disabled={disabled || !values?.unidadeSaude}
              mostrarInativas
              showSemEquipeOption
              dataTestId='EquipeSelectField.InativacaoImoveis'
            />
          </Cell>
          <Cell size={2}>
            <MicroareaImovelSelectField
              name={meta.microarea}
              label='Microárea'
              cnes={values?.unidadeSaude?.cnes ?? ''}
              ine={values?.equipe?.ine ?? ''}
              tipoEndereco={TipoEnderecoEnum.LOGRADOURO}
              disabled={disabled || !values?.equipe}
            />
          </Cell>
          <Cell size={1} />
          <Cell size={4}>
            <Flow direction='horizontal'>
              <div style={{ marginTop: 25 }}>
                <Button onClick={() => handleLimparCampos(form)} size='small' kind='normal' disabled={disabled}>
                  Limpar busca
                </Button>
              </div>
              <div style={{ marginTop: 25 }}>
                <SubmitButton
                  size='small'
                  kind='primary'
                  handleSubmit={handleSubmit}
                  disabled={disabled || isSubmitButtonDisable(values)}
                >
                  <Icon
                    icon='zoomOutline'
                    style={css`
                      margin-right: 0.5rem;
                    `}
                  />
                  Buscar imóveis
                </SubmitButton>
              </div>
            </Flow>
          </Cell>
        </Grid>
      </HeadingSection>
    )
  }

  return (
    <Form<InativacaoImoveisBuscaFormModel>
      render={renderForm}
      onSubmit={onSubmit}
      validate={validateInativacaoImoveisBuscaForm}
      initialValues={INITIAL_VALUES}
      decorators={decorators}
    />
  )
}

export const isSubmitButtonDisable = (values: InativacaoImoveisBuscaFormModel) =>
  [values.logradouro, values.cep, values.unidadeSaude, values.microarea].every(isEmpty)
